import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Mail, Building, Star, CheckCircle, ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const EvaluationForm = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();
  
  const [event, setEvent] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [evaluationGroups, setEvaluationGroups] = useState([]);
  const [eventQuestions, setEventQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ratings, setRatings] = useState({});
  const [eventResponses, setEventResponses] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [certTemplates, setCertTemplates] = useState([]);
  
  // Pagination state
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [showPagination, setShowPagination] = useState(true);
  
  // Toggle between paginated and full view
  const togglePagination = () => {
    setShowPagination(!showPagination);
    setCurrentGroupIndex(0);
  };

  useEffect(() => {
    fetchFormData();
  }, [eventId]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      
      // Fetch event details, divisions, evaluation groups, evaluation items, and event questions
      const [eventRes, divisionsRes, groupsRes, itemsRes, questionsRes, certsRes] = await Promise.all([
        api.get('/simple.php?action=public-events'),
        api.get('/simple.php?action=public-divisions'),
        api.get('/simple.php?action=evaluation-groups'),
        api.get('/simple.php?action=evaluation-items'),
        api.get(`/simple.php?action=event-questions&event_id=${eventId}`),
        api.get(`/simple.php?action=event-certificates&event_id=${eventId}`),
      ]);

      // Find the specific event
      const eventData = eventRes.data.data.find(e => e.events_id == eventId);
      if (!eventData) {
        toast.error('Event not found or not active');
        navigate('/');
        return;
      }

      setEvent(eventData);
      setDivisions(divisionsRes.data.data);
      
      // Organize evaluation items by groups
      const groups = groupsRes.data.data;
      const items = itemsRes.data.data;
      
      // Add items to their respective groups
      const groupsWithItems = groups.map(group => ({
        ...group,
        items: items.filter(item => item.evaluation_group_id === group.evaluation_group_id)
      }));
      
      setEvaluationGroups(groupsWithItems);
      setEventQuestions(questionsRes.data.data || []);
      const activeCerts = (certsRes.data.data || []).filter(c => c.is_active);
      setCertTemplates(activeCerts);
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load evaluation form');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (itemId, rating) => {
    // Convert rating text to numeric value for storage
    let numericRating = null;
    let ratingText = null;
    
    if (typeof rating === 'string') {
      // It's a descriptive rating, convert to number
      ratingText = rating;
      switch (rating) {
        case 'Strongly Agree':
          numericRating = 4;
          break;
        case 'Agree':
          numericRating = 3;
          break;
        case 'Disagree':
          numericRating = 2;
          break;
        case 'Strongly Disagree':
          numericRating = 1;
          break;
        case 'Not Applicable':
          numericRating = null;
          break;
        default:
          numericRating = 3; // Default to Agree
      }
    } else {
      // It's already numeric
      numericRating = rating;
      ratingText = null;
    }
    
    setRatings(prev => ({
      ...prev,
      [itemId]: {
        numeric: numericRating,
        text: ratingText,
        original: rating
      }
    }));
  };

  const handleEventResponseChange = (questionId, response) => {
    setEventResponses(prev => ({
      ...prev,
      [questionId]: response
    }));
  };

  // Get descriptive rating options
  const getRatingOptions = (item) => {
    if (item.rating_type === 'numeric') {
      return Array.from({ length: item.rating_scale }, (_, i) => ({
        value: i + 1,
        label: (i + 1).toString()
      }));
    }
    
    // Default to descriptive ratings
    return [
      { value: 'Strongly Agree', label: 'Strongly Agree' },
      { value: 'Agree', label: 'Agree' },
      { value: 'Disagree', label: 'Disagree' },
      { value: 'Strongly Disagree', label: 'Strongly Disagree' },
      { value: 'Not Applicable', label: 'Not Applicable' }
    ];
  };

  const handleNext = () => {
    const totalSteps = evaluationGroups.length + (eventQuestions.length > 0 ? 1 : 0);
    if (currentGroupIndex < totalSteps - 1) {
      setCurrentGroupIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex(prev => prev - 1);
    }
  };

  const getCurrentGroupProgress = () => {
    if (!showPagination) return 100;
    const totalSteps = evaluationGroups.length + (eventQuestions.length > 0 ? 1 : 0);
    return ((currentGroupIndex + 1) / totalSteps) * 100;
  };

  const isGroupComplete = (groupIndex) => {
    // If it's the event questions step
    if (groupIndex >= evaluationGroups.length) {
      const requiredQuestions = eventQuestions.filter(q => q.is_required);
      return requiredQuestions.every(q => eventResponses[q.question_id] && eventResponses[q.question_id].trim());
    }
    
    // For evaluation groups
    const group = evaluationGroups[groupIndex];
    if (!group) return false;
    return group.items.every(item => ratings[item.evaluation_item_id]);
  };

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);

      // Validate that all required ratings are provided
      const allItems = evaluationGroups.flatMap(group => group.items);
      const responses = allItems.map(item => {
        const ratingData = ratings[item.evaluation_item_id];
        const userComments = data[`comments_${item.evaluation_item_id}`] || '';
        
        // Combine rating text and user comments for the comments field
        let combinedComments = '';
        if (ratingData?.text && userComments) {
          combinedComments = `Rating: ${ratingData.text}. Comments: ${userComments}`;
        } else if (ratingData?.text) {
          combinedComments = `Rating: ${ratingData.text}`;
        } else if (userComments) {
          combinedComments = userComments;
        }
        
        return {
          evaluation_item_id: item.evaluation_item_id,
          rating: ratingData?.numeric || 3, // Default to 3 if no numeric rating
          comments: combinedComments
        };
      });

      const missingRatings = responses.filter(r => !r.rating);
      if (missingRatings.length > 0) {
        toast.error('Please provide ratings for all evaluation items');
        setSubmitting(false);
        return;
      }

      // Prepare event question responses
      const eventQuestionResponses = eventQuestions.map(q => ({
        question_id: q.question_id,
        response_text: eventResponses[q.question_id] || ''
      }));

      // Validate required event questions
      const missingEventResponses = eventQuestions.filter(q => 
        q.is_required && (!eventResponses[q.question_id] || !eventResponses[q.question_id].toString().trim())
      );
      
      if (missingEventResponses.length > 0) {
        toast.error('Please answer all required questions');
        setSubmitting(false);
        return;
      }

      // Submit evaluation
      const submissionData = {
        events_id: parseInt(eventId),
        division_id: parseInt(data.division_id),
        first_name: data.first_name,
        last_name: data.last_name,
        middle_name: data.middle_name || null,
        sex: data.sex,
        email: data.email,
        responses: responses,
        event_responses: eventQuestionResponses
      };

      console.log('Submitting evaluation data:', submissionData);

      // Show email confirmation modal before actually submitting
      setPendingSubmitData(submissionData);
      setSubmitting(false);
      setShowEmailModal(true);
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      
      // More detailed error handling
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || 'Validation error occurred';
        const validationErrors = error.response?.data?.errors;
        
        if (validationErrors && validationErrors.length > 0) {
          console.error('Validation errors:', validationErrors);
          toast.error(`Validation error: ${validationErrors[0].msg}`);
        } else {
          toast.error(errorMessage);
        }
      } else if (error.response?.status === 500) {
        toast.error('Server error occurred. Please try again later.');
      } else if (!error.response) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to submit evaluation');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailConfirm = async () => {
    try {
      setSubmitting(true);
      setShowEmailModal(false);
      const emailToSend = pendingSubmitData.email;
      await api.post('/simple.php?action=submit-evaluation', pendingSubmitData);
      toast.success('Evaluation submitted successfully!');
      setSubmittedEmail(emailToSend);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      toast.error(error.response?.data?.message || 'Failed to submit evaluation');
    } finally {
      setSubmitting(false);
      setPendingSubmitData(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="loading">Loading evaluation form...</div>
      </div>
    );
  }

  if (submitted) {
    const hasCerts = certTemplates.length > 0;
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="card" style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <CheckCircle size={64} style={{ color: '#10b981', marginBottom: '24px' }} />
          <h2 style={{ color: '#1f2937', marginBottom: '16px' }}>Thank You!</h2>
          <p style={{ color: '#6b7280', marginBottom: '8px', fontSize: '16px' }}>
            Your evaluation for <strong>{event.event_name}</strong> has been submitted successfully. 
            Your feedback helps us improve our services.
          </p>
          {hasCerts ? (
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '15px' }}>
              {certTemplates.length === 1
                ? <>Your <strong>{certTemplates[0].cert_title}</strong> has been sent to <strong>{submittedEmail}</strong>.</>
                : <>Your {certTemplates.map((c, i) => (
                    <span key={c.cert_id}>
                      {i > 0 && (i === certTemplates.length - 1 ? ' and ' : ', ')}
                      <strong>{c.cert_title}</strong>
                    </span>
                  ))} have been sent to <strong>{submittedEmail}</strong>.</>
              }
              {' '}Please check your inbox (and spam folder).
            </p>
          ) : (
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '15px' }}>
              No certificate templates are configured for this event yet.
            </p>
          )}
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            <ArrowLeft size={16} />
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px 20px' }}>

      {/* Email Confirmation Modal */}
      {showEmailModal && pendingSubmitData && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '12px',
            padding: '32px', maxWidth: '440px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <Mail size={28} style={{ color: '#3b82f6', flexShrink: 0 }} />
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.2rem', fontWeight: '700' }}>
                Confirm Your Email Address
              </h3>
            </div>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px', lineHeight: '1.6' }}>
              {certTemplates.length > 0 ? (
                <>
                  Your {certTemplates.length === 1
                    ? <strong>{certTemplates[0].cert_title}</strong>
                    : <>{certTemplates.map((c, i) => (
                        <span key={c.cert_id}>
                          {i > 0 && (i === certTemplates.length - 1 ? ' and ' : ', ')}
                          <strong>{c.cert_title}</strong>
                        </span>
                      ))}</>
                  } will be sent to:
                </>
              ) : (
                <>Your certificate(s) will be sent to:</>
              )}
            </p>
            <div style={{
              background: '#eff6ff', border: '2px solid #3b82f6',
              borderRadius: '8px', padding: '12px 16px',
              fontSize: '16px', fontWeight: '600', color: '#1d4ed8',
              textAlign: 'center', marginBottom: '16px', wordBreak: 'break-all'
            }}>
              {pendingSubmitData.email}
            </div>
            <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '24px', lineHeight: '1.5' }}>
              Please make sure this email is correct. You will not be able to change it after submitting.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowEmailModal(false); setPendingSubmitData(null); }}
              >
                ← Go Back & Edit
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleEmailConfirm}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Yes, Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Event Header */}
      <div className="card" style={{ marginBottom: '32px', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <Calendar size={24} style={{ marginRight: '12px' }} />
          <h1 style={{ margin: 0, fontSize: '1.8rem' }}>{event.event_name}</h1>
        </div>
        <p style={{ margin: 0, opacity: 0.9, fontSize: '16px' }}>
          {formatDate(event.event_datefrom)} - {formatDate(event.event_dateto)}
        </p>
        <p style={{ margin: '8px 0 0 0', opacity: 0.8, fontSize: '14px' }}>
          Please provide your honest feedback to help us improve our services.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Personal Information */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <h3 style={{ color: '#1f2937', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
            <User size={20} style={{ marginRight: '8px', display: 'inline-block' }} />
            Personal Information
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                className="form-input"
                {...register('first_name', { required: 'First name is required' })}
                placeholder="Enter your first name"
              />
              {errors.first_name && <div className="form-error">{errors.first_name.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                className="form-input"
                {...register('last_name', { required: 'Last name is required' })}
                placeholder="Enter your last name"
              />
              {errors.last_name && <div className="form-error">{errors.last_name.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Middle Name</label>
              <input
                type="text"
                className="form-input"
                {...register('middle_name')}
                placeholder="Enter your middle name (optional)"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Sex *</label>
              <select
                className="form-input"
                {...register('sex', { required: 'Please select your sex' })}
              >
                <option value="">Select your sex</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.sex && <div className="form-error">{errors.sex.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <Mail size={16} style={{ marginRight: '4px', display: 'inline-block' }} />
                Email Address *
              </label>
              <input
                type="email"
                className="form-input"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address'
                  }
                })}
                placeholder="Enter your email address"
              />
              {errors.email && <div className="form-error">{errors.email.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <Building size={16} style={{ marginRight: '4px', display: 'inline-block' }} />
                Division *
              </label>
              <select
                className="form-input"
                {...register('division_id', { required: 'Please select your division' })}
              >
                <option value="">Select your division</option>
                {divisions.map(division => (
                  <option key={division.division_id} value={division.division_id}>
                    {division.division_name}
                  </option>
                ))}
              </select>
              {errors.division_id && <div className="form-error">{errors.division_id.message}</div>}
            </div>
          </div>
        </div>

        {/* Progress Bar and Navigation */}
        {showPagination && evaluationGroups.length > 1 && (
          <div className="card" style={{ marginBottom: '24px', background: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h4 style={{ margin: 0, color: '#1f2937' }}>
                {currentGroupIndex >= evaluationGroups.length ? (
                  `Step ${currentGroupIndex + 1} of ${evaluationGroups.length + (eventQuestions.length > 0 ? 1 : 0)}: Additional Questions`
                ) : (
                  `Step ${currentGroupIndex + 1} of ${evaluationGroups.length + (eventQuestions.length > 0 ? 1 : 0)}: ${evaluationGroups[currentGroupIndex]?.evaluation_group}`
                )}
              </h4>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={togglePagination}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                {showPagination ? 'Show All' : 'Show Steps'}
              </button>
            </div>
            
            {/* Progress Bar */}
            <div style={{ 
              width: '100%', 
              height: '8px', 
              backgroundColor: '#e5e7eb', 
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: `${getCurrentGroupProgress()}%`,
                height: '100%',
                backgroundColor: '#3b82f6',
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            
            {/* Step Indicators */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {[...evaluationGroups, ...(eventQuestions.length > 0 ? [{ evaluation_group_id: 'questions', evaluation_group: 'Additional Questions' }] : [])].map((group, index) => (
                <div key={group.evaluation_group_id} style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: index === currentGroupIndex 
                        ? '#3b82f6' 
                        : isGroupComplete(index) 
                          ? '#10b981' 
                          : '#e5e7eb',
                      color: index === currentGroupIndex || isGroupComplete(index) ? 'white' : '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    onClick={() => setCurrentGroupIndex(index)}
                    title={group.evaluation_group}
                  >
                    {isGroupComplete(index) ? '✓' : index + 1}
                  </div>
                  {index < (evaluationGroups.length + (eventQuestions.length > 0 ? 1 : 0)) - 1 && (
                    <div style={{
                      width: '24px',
                      height: '2px',
                      backgroundColor: index < currentGroupIndex ? '#10b981' : '#e5e7eb',
                      margin: '0 4px'
                    }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Evaluation Groups */}
        {showPagination ? (
          // Paginated View - Show one group at a time or event questions
          currentGroupIndex >= evaluationGroups.length ? (
            // Show Event Questions Step
            eventQuestions.length > 0 && (
              <div className="evaluation-group">
                <h3>Additional Questions</h3>
                
                {eventQuestions.map((question, index) => (
                  <div key={question.question_id} className="form-group" style={{ marginBottom: '24px' }}>
                    <label className="form-label" style={{ fontSize: '15px', fontWeight: '500' }}>
                      {index + 1}. {question.question_text} {question.is_required && <span style={{ color: '#ef4444' }}>*</span>}
                    </label>
                    <textarea
                      className="form-input"
                      rows="4"
                      placeholder="Your answer..."
                      value={eventResponses[question.question_id] || ''}
                      onChange={(e) => handleEventResponseChange(question.question_id, e.target.value)}
                      required={question.is_required}
                      style={{ fontSize: '14px', resize: 'vertical' }}
                    />
                  </div>
                ))}
                
                {/* Navigation Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePrevious}
                    disabled={currentGroupIndex === 0}
                  >
                    ← Previous
                  </button>
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting || !isGroupComplete(currentGroupIndex)}
                    >
                      {submitting ? 'Submitting...' : 'Submit Evaluation'}
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : (
            // Show Evaluation Groups
            evaluationGroups[currentGroupIndex] && (
            <div key={evaluationGroups[currentGroupIndex].evaluation_group_id} className="evaluation-group">
              <h3>
                {evaluationGroups[currentGroupIndex].evaluation_group}
              </h3>

              {evaluationGroups[currentGroupIndex].items.map((item, itemIndex) => (
                <div key={item.evaluation_item_id} className="evaluation-item">
                  <div style={{ marginBottom: '12px' }}>
                    <label className="form-label" style={{ fontSize: '15px', fontWeight: '500' }}>
                      {itemIndex + 1}. {item.evaluation_item}
                    </label>
                  </div>

                  <div className="rating-group">
                    {getRatingOptions(item).map((option) => {
                      const ratingData = ratings[item.evaluation_item_id];
                      const isSelected = ratingData?.original === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`rating-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleRatingChange(item.evaluation_item_id, option.value)}
                          style={{ minWidth: '120px', fontSize: '13px' }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {/* Event Questions — shown inline on the last group page */}
              {currentGroupIndex === evaluationGroups.length - 1 && eventQuestions.length > 0 && (
                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e5e7eb' }}>
                  <h3 style={{ color: '#1f2937', marginBottom: '20px', fontSize: '1.1rem' }}>
                    Additional Questions
                  </h3>
                  {eventQuestions.map((question, index) => (
                    <div key={question.question_id} className="form-group" style={{ marginBottom: '24px' }}>
                      <label className="form-label" style={{ fontSize: '15px', fontWeight: '500' }}>
                        {index + 1}. {question.question_text}{question.is_required && <span style={{ color: '#ef4444' }}> *</span>}
                      </label>
                      <textarea
                        className="form-input"
                        rows="4"
                        placeholder="Your answer..."
                        value={eventResponses[question.question_id] || ''}
                        onChange={(e) => handleEventResponseChange(question.question_id, e.target.value)}
                        style={{ fontSize: '14px', resize: 'vertical' }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handlePrevious}
                  disabled={currentGroupIndex === 0}
                  style={{ visibility: currentGroupIndex === 0 ? 'hidden' : 'visible' }}
                >
                  ← Previous
                </button>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  {currentGroupIndex === evaluationGroups.length - 1 ? (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting || !isGroupComplete(currentGroupIndex)}
                    >
                      {submitting ? 'Submitting...' : 'Submit Evaluation'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleNext}
                      disabled={!isGroupComplete(currentGroupIndex)}
                    >
                      Next →
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          // Full View - Show all groups
          evaluationGroups.map((group, groupIndex) => (
            <div key={group.evaluation_group_id} className="evaluation-group">
              <h3>
                {groupIndex + 1}. {group.evaluation_group}
              </h3>

              {group.items.map((item, itemIndex) => (
                <div key={item.evaluation_item_id} className="evaluation-item">
                  <div style={{ marginBottom: '12px' }}>
                    <label className="form-label" style={{ fontSize: '15px', fontWeight: '500' }}>
                      {groupIndex + 1}.{itemIndex + 1} {item.evaluation_item}
                    </label>
                  </div>

                  <div className="rating-group">
                    {getRatingOptions(item).map((option) => {
                      const ratingData = ratings[item.evaluation_item_id];
                      const isSelected = ratingData?.original === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`rating-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleRatingChange(item.evaluation_item_id, option.value)}
                          style={{ minWidth: '120px', fontSize: '13px' }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}

        {/* Event-Specific Questions - Only show in full view mode */}
        {!showPagination && eventQuestions.length > 0 && (
          <div className="card" style={{ marginTop: '32px' }}>
            <h3 style={{ color: '#1f2937', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
              Additional Questions
            </h3>
            
            {eventQuestions.map((question, index) => (
              <div key={question.question_id} className="form-group" style={{ marginBottom: '24px' }}>
                <label className="form-label" style={{ fontSize: '15px', fontWeight: '500' }}>
                  {index + 1}. {question.question_text} {question.is_required && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                <textarea
                  className="form-input"
                  rows="4"
                  placeholder="Your answer..."
                  value={eventResponses[question.question_id] || ''}
                  onChange={(e) => handleEventResponseChange(question.question_id, e.target.value)}
                  required={question.is_required}
                  style={{ fontSize: '14px', resize: 'vertical' }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Submit Section - Only show in full view or on last step */}
        {(!showPagination || (currentGroupIndex === evaluationGroups.length - 1 && eventQuestions.length === 0) || 
          (showPagination && currentGroupIndex === evaluationGroups.length && eventQuestions.length > 0)) && (
          <div className="card" style={{ marginTop: '32px', textAlign: 'center' }}>
            <h4 style={{ color: '#1f2937', marginBottom: '16px' }}>Ready to Submit?</h4>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Please review your responses before submitting. Once submitted, you cannot modify your evaluation.
            </p>
            
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/')}
                disabled={submitting}
              >
                <ArrowLeft size={16} />
                Cancel
              </button>
              {!showPagination && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ minWidth: '160px' }}
                >
                  {submitting ? 'Submitting...' : 'Submit Evaluation'}
                </button>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default EvaluationForm;